import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getAnyGame, listGames, type MahjongView } from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { Icon, type IconName } from '../ui/Icon'
import { GameScreen } from './GameScreen'
import { MahjongScreen } from './MahjongScreen'
import { kindOf } from './gameKinds'
import { closeGame, knownKind, onGameNav, openGame, openGameId, rememberKind } from './gameNav'
import { noticeFor, seatName } from './gameText'
import { gamesOn, mahjongOn } from './flag'
import './games.css'

/** `invite`: an invitation still waiting on this person — re-derived from the games list, so it can also go away. */
type Notice = { id: string; text: string; icon: IconName; invite?: boolean }

/**
 * The open game, if the URL names one (`?game=<id>`), over everything else — and, wherever the app is, word of what
 * the other player did in a game you are not looking at (owner, 2026-10-19: 當使用者退出棋局的時候對方也要收到通知;
 * an invitation from a friend, their answer, a message). A notice stays until it is opened or closed: nothing here
 * runs on a clock.
 *
 * A FRAME IS THE FAST PATH, NOT THE ONLY ONE (owner, 2026-10-19: 我進入棋局了, 我的好朋友卻沒有進入, 也沒有收到邀請). The
 * friend's socket was between reconnects at the moment the invitation's frame went out, and nothing ever asked
 * again. So the invitations waiting on this person are READ when the app starts and every time its socket (re)opens
 * — the protocol's reconcile-on-open — and shown until answered, opened or dismissed.
 */
export function GameHost() {
  const [id, setId] = useState<string | null>(() => openGameId())
  const [notices, setNotices] = useState<Notice[]>([])
  const openRef = useRef(id)
  openRef.current = id
  // Notices the person closed this visit: a reconcile does not bring them back.
  const dismissed = useRef(new Set<string>())
  useEffect(() => {
    if (!gamesOn()) return
    let alive = true
    const reconcile = async () => {
      let games
      let all: MahjongView[]
      try {
        const r = await listGames()
        games = r.games
        all = mahjongOn() ? (r.mahjong ?? []) : []
      } catch {
        return // not signed in yet, or the chat is away: the next open asks again
      }
      if (!alive) return
      const waiting = games.filter(
        (g) => g.status === 'invited' && g.invited === 'them' && !dismissed.current.has(g.id) && g.id !== openRef.current,
      )
      // A mahjong table waiting on THIS person's answer (others may have answered already).
      const tables = all.filter(
        (t) =>
          t.status === 'invited' &&
          t.you !== null &&
          t.seats[t.you]?.invite === 'pending' &&
          !dismissed.current.has(t.id) &&
          t.id !== openRef.current,
      )
      const pending = new Set([...waiting.map((g) => g.id), ...tables.map((t) => t.id)])
      setNotices((ns) => {
        // An invitation answered elsewhere (another device) stops being news.
        const kept = ns.filter((n) => !n.invite || pending.has(n.id))
        const known = new Set(kept.map((n) => n.id))
        const fresh = waiting
          .filter((g) => !known.has(g.id))
          .map((g) => ({
            id: g.id,
            text: `${seatName(g, g.you === 'w' ? 'b' : 'w')} invites you to a game of ${kindOf(g.type).noun}`,
            icon: kindOf(g.type).icon,
            invite: true,
          }))
        const freshTables = tables
          .filter((t) => !known.has(t.id))
          .map((t) => ({ id: t.id, text: `${inviterOf(t)} invites you to a game of mahjong`, icon: 'style' as IconName, invite: true }))
        return [...kept, ...fresh, ...freshTables].slice(-3)
      })
    }
    void reconcile()
    const off = onChatv1Open(() => void reconcile())
    return () => {
      alive = false
      off()
    }
  }, [])
  useEffect(() => onGameNav(() => setId(openGameId())), [])
  useEffect(
    () =>
      onChatv1Frame((f) => {
        if (f.type === 'mahjong' && mahjongOn()) {
          rememberKind(f.game.id, 'mahjong')
          if (openRef.current === f.game.id) return
          const text = tableNotice(f.game, f.event)
          if (!text) return
          const invite = f.event?.kind === 'invited'
          setNotices((ns) => [...ns.filter((n) => n.id !== f.game.id), { id: f.game.id, text, icon: 'style' as IconName, ...(invite ? { invite } : {}) }].slice(-3))
          return
        }
        if (f.type !== 'game' || !gamesOn()) return
        if (openRef.current === f.game.id) return // on the board already
        const text = noticeFor(f.game, f.event)
        if (!text) return
        // One notice per game, the newest — three at most on screen.
        const invite = f.event?.kind === 'invited'
        const icon = kindOf(f.game.type).icon
        setNotices((ns) => [...ns.filter((n) => n.id !== f.game.id), { id: f.game.id, text, icon, ...(invite ? { invite } : {}) }].slice(-3))
      }),
    [],
  )
  // Opening a game answers its notice.
  useEffect(() => {
    if (!id) return
    dismissed.current.add(id)
    setNotices((ns) => ns.filter((n) => n.id !== id))
  }, [id])
  if (!gamesOn()) return null
  return (
    <>
      {id && <OpenGame key={id} id={id} />}
      {notices.length > 0 &&
        createPortal(
          <div className="gm-notices" role="status" aria-live="polite">
            {notices.map((n) => (
              <div key={n.id} className="gm-notice">
                <Icon name={n.icon} size={18} />
                <span className="gm-notice-text">{n.text}</span>
                <button type="button" className="fr-btn ok" onClick={() => openGame(n.id)}>
                  Open
                </button>
                <button
                  type="button"
                  className="gm-notice-x"
                  aria-label="Dismiss"
                  onClick={() => {
                    dismissed.current.add(n.id)
                    setNotices((ns) => ns.filter((x) => x.id !== n.id))
                  }}
                >
                  <Icon name="close" size={16} />
                </button>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}

/**
 * The game the URL names, on its own screen: a board's (GameScreen) or a mahjong table's (MahjongScreen). An id this
 * app has seen is known at once; any other is read once to find out — and that reading is the screen's first.
 */
function OpenGame({ id }: { id: string }) {
  const qc = useQueryClient()
  const known = knownKind(id)
  const q = useQuery({
    queryKey: ['gameKind', id],
    enabled: !known,
    staleTime: Infinity,
    queryFn: async () => {
      const r = await getAnyGame(id)
      if (r.mahjong) {
        qc.setQueryData(['mahjong', id], r.mahjong)
        rememberKind(id, 'mahjong')
        return 'mahjong' as const
      }
      if (r.game) qc.setQueryData(['game', id], r.game)
      rememberKind(id, 'board')
      return 'board' as const
    },
  })
  const kind = known ?? q.data ?? (q.isError ? 'board' : null)
  if (kind === 'mahjong' && mahjongOn()) return <MahjongScreen id={id} onClose={closeGame} />
  if (kind === 'board') return <GameScreen id={id} onClose={closeGame} />
  return null
}

/** Who sent a table's invitation: the person whose seat had none to answer. */
function inviterOf(t: MahjongView): string {
  return t.seats.find((s) => s.kind === 'user' && s.invite === null && !s.you)?.name || 'A friend'
}

/**
 * What to tell a person about someone else's act at a table they are not looking at — an invitation, an answer,
 * the game starting, someone leaving, a message — or null (their own act, or a play: not worth interrupting for).
 */
function tableNotice(t: MahjongView, ev: { kind: string; mine: boolean; seat?: number } | undefined): string | null {
  if (!ev || ev.mine || t.you === null) return null
  const who = ev.seat === undefined ? 'Someone' : t.seats[ev.seat]?.name || 'Someone'
  switch (ev.kind) {
    case 'invited':
      return `${inviterOf(t)} invites you to a game of mahjong`
    case 'accepted':
      return `${who} accepted your game of mahjong`
    case 'started':
      return 'Your game of mahjong is on'
    case 'declined':
      return `${who} declined — the computer takes the seat`
    case 'cancelled':
      return `${who} took back the invitation to play mahjong`
    case 'left':
      return `${who} left the table — the computer plays their seat`
    case 'said': {
      const last = t.chat[t.chat.length - 1]
      return last ? `${who}: ${last.text}` : null
    }
    default:
      return null
  }
}
